import {useSyncExternalStore} from 'react'

// Whether the last endpoint settings save failed. The save footer writes it;
// Settings > Endpoints and Users read it, so the status and right side show the
// saved values again and the footer says the changes weren't saved.
const SAVE_FAILED_EVENT = 'endpoint-settings:save-failed'

let hasSaveFailed = false
// Bumped on every save, Cancel and page leave, so a slow save that finishes
// after the draft was dropped can't mark the next draft as failed.
let currentSaveId = 0

const publish = (failed: boolean): void => {
  if (hasSaveFailed === failed) return

  hasSaveFailed = failed
  window.dispatchEvent(new Event(SAVE_FAILED_EVENT))
}

export const startEndpointSettingsSave = (): number => {
  currentSaveId += 1
  return currentSaveId
}

export const finishEndpointSettingsSave = (
  saveId: number,
  failed: boolean,
): void => {
  if (saveId !== currentSaveId) return
  publish(failed)
}

// Whether no Cancel, page leave or newer save has happened since this save
// started, so its draft is still the one on screen.
export const isLatestEndpointSettingsSave = (saveId: number): boolean =>
  saveId === currentSaveId

// For Cancel and page leave: drops the draft, so a save still running can't
// report on the next one.
export const setEndpointSettingsSaveFailed = (failed: boolean): void => {
  currentSaveId += 1
  publish(failed)
}

// Hides the message only. A save still running still reports its result.
export const clearEndpointSettingsSaveFailed = (): void => publish(false)

const subscribe = (listener: () => void): (() => void) => {
  window.addEventListener(SAVE_FAILED_EVENT, listener)
  return () => window.removeEventListener(SAVE_FAILED_EVENT, listener)
}

const getSnapshot = (): boolean => hasSaveFailed

export const useEndpointSettingsSaveFailed = (): boolean =>
  useSyncExternalStore(subscribe, getSnapshot)
